import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, CalendarClock, Download, Check, X, Pencil, MapPin, Printer, Users, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { QrCode } from "@/components/QrCode";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

function DailyTime({ time, loc }) {
  if (!time) return <span className="text-slate-400">—</span>;
  if (loc === "outside") return <span className="text-red-600 font-semibold" data-testid="daily-outside">{time} · Outside area</span>;
  if (loc === "unavailable") return <span className="text-slate-600">{time} <span className="text-slate-400">· Location unavailable</span></span>;
  return <span className="text-slate-700">{time}</span>;
}

async function openSelfie(eventId) {
  try {
    const { data } = await api.get(`/admin/attendance/selfie/${eventId}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.rel = "noreferrer";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 8000);
  } catch { toast.error("Could not open the selfie"); }
}

function SelfieThumb({ eventId, keyName }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let active = true; let url;
    api.get(`/admin/attendance/selfie/${eventId}`, { responseType: "blob" })
      .then(({ data }) => { if (!active) return; url = URL.createObjectURL(data); setSrc(url); })
      .catch(() => { if (active) setErr(true); });
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [eventId]);
  if (err) return <div className="h-28 w-28 shrink-0 rounded-lg bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-400">Unavailable</div>;
  if (!src) return <div className="h-28 w-28 shrink-0 rounded-lg bg-slate-100 animate-pulse" data-testid={`evidence-selfie-loading-${keyName}`} />;
  return <img src={src} alt="Live selfie" onClick={() => window.open(src, "_blank", "noopener")} className="h-28 w-28 shrink-0 rounded-lg object-cover border border-slate-200 cursor-zoom-in hover:ring-2 hover:ring-blue-400 transition" data-testid={`evidence-selfie-img-${keyName}`} />;
}

function AttendanceEvidence({ label, ev }) {
  if (!ev) return null;
  const key = label.toLowerCase();
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`evidence-${key}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{label}</span>
        {ev.verified
          ? <Badge className="bg-green-600 hover:bg-green-600" data-testid={`evidence-status-${key}`}>Location Verified ✓</Badge>
          : <Badge className="bg-red-600 hover:bg-red-600" data-testid={`evidence-status-${key}`}>Verification Failed ✗</Badge>}
      </div>
      <div className="flex gap-3">
        {ev.has_selfie
          ? <SelfieThumb eventId={ev.event_id} keyName={key} />
          : <div className="h-28 w-28 shrink-0 rounded-lg bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-400 text-center p-1" data-testid={`evidence-noselfie-${key}`}>No selfie<br />on record</div>}
        <div className="flex-1 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-slate-600 content-start">
          <div className="col-span-2"><span className="text-slate-400 block">Server date &amp; time</span><span className="font-semibold text-slate-800" data-testid={`evidence-datetime-${key}`}>{ev.datetime || ev.time || "—"}</span></div>
          <div><span className="text-slate-400 block">Distance</span><span data-testid={`evidence-distance-${key}`}>{ev.distance_m != null ? `${ev.distance_m} m` : "—"}</span></div>
          <div><span className="text-slate-400 block">GPS accuracy</span><span data-testid={`evidence-accuracy-${key}`}>{ev.accuracy != null ? `±${Math.round(ev.accuracy)} m` : "—"}</span></div>
          <div className="col-span-2"><span className="text-slate-400 block">Latitude, Longitude</span><span data-testid={`evidence-latlng-${key}`}>{ev.lat != null && ev.lng != null ? `${Number(ev.lat).toFixed(6)}, ${Number(ev.lng).toFixed(6)}` : "—"}</span></div>
        </div>
      </div>
      {ev.has_selfie && <Button size="sm" variant="ghost" className="mt-1.5 h-7 text-xs text-blue-700 hover:text-blue-800" onClick={() => openSelfie(ev.event_id)} data-testid={`evidence-selfie-${key}`}><Camera className="h-3.5 w-3.5 mr-1" /> Open full selfie</Button>}
    </div>
  );
}

export default function AttendancePanel() {
  const [teachers, setTeachers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tid, setTid] = useState("");
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([{ start: "08:00", end: "10:00" }]);
  const [shifts, setShifts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [report, setReport] = useState(null);
  const [rFrom, setRFrom] = useState("");
  const [rTo, setRTo] = useState("");
  const [dailyDate, setDailyDate] = useState(today());
  const [daily, setDaily] = useState(null);
  const [dailyBusy, setDailyBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadBase = useCallback(async () => {
    try {
      const [t, s, req] = await Promise.all([api.get("/admin/teachers"), api.get("/admin/attendance/settings"), api.get("/admin/shift-changes")]);
      setTeachers(t.data); setSettings(s.data); setRequests(req.data);
      if (!tid && t.data[0]) setTid(t.data[0].id);
    } catch { toast.error("Could not load"); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line
  useEffect(() => { loadBase(); }, [loadBase]);

  const loadShifts = useCallback(async () => {
    if (!tid) return;
    const { data } = await api.get("/admin/shifts", { params: { teacher_id: tid } });
    setShifts(data);
  }, [tid]);
  useEffect(() => { loadShifts(); }, [loadShifts]);

  const saveSettings = async () => {
    try {
      await api.put("/admin/attendance/settings", settings);
      toast.success("Attendance settings saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please enter valid latitude, longitude and radius values.");
    }
  };
  const createShifts = async () => {
    try { const { data } = await api.post("/admin/shifts", { teacher_id: tid, date, shifts: rows.filter(r => r.start && r.end) }); toast.success(`${data.created.length} shift(s) created`); setRows([{ start: "08:00", end: "10:00" }]); loadShifts(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const markDay = async (type) => {
    try { await api.post("/admin/shifts/mark-day", { teacher_id: tid, date, type }); toast.success(`Marked ${type}`); loadShifts(); }
    catch { toast.error("Failed"); }
  };
  const cancelShift = async (id) => { try { await api.post(`/admin/shifts/${id}/cancel`); toast.success("Shift cancelled"); loadShifts(); } catch { toast.error("Failed"); } };
  const decide = async (rid, decision, fs, fe) => {
    try { await api.post(`/admin/shift-changes/${rid}/decision`, { decision, final_start: fs, final_end: fe }); toast.success("Decision saved"); loadBase(); loadShifts(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const runReport = async () => {
    const { data } = await api.get("/admin/attendance/report", { params: { teacher_id: tid || undefined, from_date: rFrom || undefined, to_date: rTo || undefined } });
    setReport(data);
  };
  const downloadCsv = async () => {
    const res = await api.get("/admin/attendance/report.csv", { params: { teacher_id: tid || undefined, from_date: rFrom || undefined, to_date: rTo || undefined }, responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "ptg_attendance.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const runDaily = async () => {
    setDailyBusy(true);
    try { const { data } = await api.get("/admin/attendance/daily", { params: { date: dailyDate } }); setDaily(data); }
    catch { toast.error("Could not load the daily report"); }
    finally { setDailyBusy(false); }
  };
  const downloadDailyCsv = async () => {
    const res = await api.get("/admin/attendance/daily.csv", { params: { date: dailyDate }, responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `ptg_daily_attendance_${dailyDate}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const printDaily = () => {
    if (!daily) { toast.error("Run the report first"); return; }
    const LOC = { outside: "Outside area", unavailable: "Location unavailable", inside: "Inside area" };
    const cell = (time, loc) => {
      if (!time) return "—";
      const red = loc === "outside";
      const extra = loc === "outside" ? " · Outside area" : loc === "unavailable" ? " · Location unavailable" : "";
      return `<span style="${red ? "color:#dc2626;font-weight:700" : ""}">${time}${extra}</span>`;
    };
    const status = (s) => ({ completed: "Completed", incomplete: "Incomplete (no clock-out)", missing_clock_in: "Missing clock-in", no_schedule: "No scheduled shift", cancelled: "Cancelled", leave: "On leave", holiday: "Holiday" }[s] || s);
    let body = "";
    daily.teachers.forEach((t) => {
      body += `<tr style="background:#f1f5f9"><td colspan="7" style="padding:6px 8px;font-weight:700">${t.teacher_name} — Daily total: ${t.daily_total_label}</td></tr>`;
      t.shifts.forEach((s) => {
        const sched = s.start ? `${s.start}–${s.end}` : (s.shift_type === "leave" || s.shift_type === "holiday" ? status(s.status) : "—");
        const flags = [s.late_min ? `Late ${s.late_min}m` : "", s.early_min ? `Left ${s.early_min}m early` : ""].filter(Boolean).join(", ");
        body += `<tr>
          <td>${sched}</td>
          <td>${cell(s.clock_in, s.clock_in_loc)}</td>
          <td>${cell(s.clock_out, s.clock_out_loc)}</td>
          <td>${s.worked_label || "—"}</td>
          <td>${flags || "—"}</td>
          <td>${status(s.status)}</td>
        </tr>`;
      });
    });
    const html = `<html><head><title>PTG Daily Attendance — ${formatDate(daily.date)}</title>
      <style>body{font-family:Arial,sans-serif;color:#0f172a;padding:24px}h2{margin:0 0 4px}
      table{width:100%;border-collapse:collapse;font-size:13px;margin-top:12px}
      th,td{border:1px solid #e2e8f0;padding:6px 8px;text-align:left}
      th{background:#1e3a8a;color:#fff}</style></head>
      <body><h2>PTG — All Teachers Daily Attendance</h2>
      <p>Date: ${formatDate(daily.date)} (Asia/Kolkata) · Permitted radius: ${daily.radius_m} m. Red = recorded outside the permitted area. A missing clock-in is for review and is not an automatic absence.</p>
      <table><thead><tr><th>Scheduled</th><th>Clock In</th><th>Clock Out</th><th>Worked</th><th>Late / Early</th><th>Status</th></tr></thead>
      <tbody>${body}</tbody></table></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to print"); return; }
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 400);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;
  const pending = requests.filter(r => r.status === "pending");

  return (
    <div data-testid="panel-attendance">
      <h2 className="text-2xl font-bold text-slate-900 mb-1">Teacher Attendance</h2>
      <p className="text-sm text-slate-500 mb-5">Set the PTG location, build shift schedules, review shift-change requests and export reports. Times are in Asia/Kolkata (IST).</p>

      <Tabs defaultValue="schedule">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="schedule" data-testid="att-tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="requests" data-testid="att-tab-requests">Change Requests {pending.length > 0 && <Badge className="ml-1.5 bg-amber-500">{pending.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="daily" data-testid="att-tab-daily">All Teachers — Daily</TabsTrigger>
          <TabsTrigger value="reports" data-testid="att-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="settings" data-testid="att-tab-settings">Location</TabsTrigger>
          <TabsTrigger value="officeqr" data-testid="att-tab-qr">Office QR</TabsTrigger>
        </TabsList>

        {/* LOCATION */}
        <TabsContent value="settings">
          <div className="rounded-xl border border-slate-200 bg-white p-5 max-w-xl space-y-3" data-testid="location-settings">
            <p className="flex items-center gap-2 font-semibold text-slate-800"><MapPin className="h-4 w-4" /> Official PTG workplace location</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-sm font-semibold">Latitude</Label><Input type="number" step="any" value={settings.lat ?? ""} onChange={(e) => setSettings(s => ({ ...s, lat: e.target.value === "" ? null : parseFloat(e.target.value) }))} data-testid="set-lat" /></div>
              <div><Label className="mb-1 block text-sm font-semibold">Longitude</Label><Input type="number" step="any" value={settings.lng ?? ""} onChange={(e) => setSettings(s => ({ ...s, lng: e.target.value === "" ? null : parseFloat(e.target.value) }))} data-testid="set-lng" /></div>
              <div><Label className="mb-1 block text-sm font-semibold">Allowed radius (m)</Label><Input type="number" value={settings.radius_m ?? ""} onChange={(e) => setSettings(s => ({ ...s, radius_m: e.target.value === "" ? null : parseInt(e.target.value) }))} data-testid="set-radius" /></div>
              <div><Label className="mb-1 block text-sm font-semibold">GPS accuracy limit (m)</Label><Input type="number" value={settings.accuracy_threshold_m ?? ""} onChange={(e) => setSettings(s => ({ ...s, accuracy_threshold_m: e.target.value === "" ? null : parseInt(e.target.value) }))} data-testid="set-accuracy" /></div>
            </div>
            <p className="text-xs text-slate-400">Tip: open Google Maps, right-click your PTG building and copy the latitude, longitude.</p>
            <Button onClick={saveSettings} className="bg-blue-900 hover:bg-blue-950" data-testid="save-settings">Save location</Button>
          </div>
        </TabsContent>

        {/* SCHEDULE */}
        <TabsContent value="officeqr">
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="office-qr-panel">
            <h3 className="font-bold text-slate-900">PTG Office Attendance QR</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4 max-w-xl">Print this one QR and stick it on the office door. Every teacher scans this same QR with their own phone, then logs in and confirms Clock In / Clock Out. Students, visitors and non-teachers cannot record attendance. This QR opens: <code className="bg-slate-100 px-1 rounded break-all">{`${window.location.origin}/attendance`}</code></p>
            <QrCode value={`${window.location.origin}/attendance`} label="PTG Office Attendance QR" title="Clock In / Out – Teachers" size={260} caption={`Scan to open ${window.location.origin}/attendance — teacher login required`} />
            <p className="text-xs text-slate-400 mt-4 max-w-xl">Note: this QR points to the website's current address. After you publish to your live/custom domain, reopen this tab and reprint so the QR points to the live domain.</p>
          </div>
        </TabsContent>

        <TabsContent value="schedule">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1 block text-sm font-semibold">Teacher</Label>
                  <Select value={tid} onValueChange={setTid}><SelectTrigger data-testid="att-teacher-select"><SelectValue placeholder="Select teacher" /></SelectTrigger>
                    <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id} data-testid={`att-teacher-${t.id}`}>{t.name} · {t.email}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="mb-1 block text-sm font-semibold">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="att-date" /></div>
              </div>
              <Label className="block text-sm font-semibold">Shifts for this day</Label>
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`shift-row-${i}`}>
                  <Input type="time" value={r.start} onChange={(e) => setRows(rs => rs.map((x, j) => j === i ? { ...x, start: e.target.value } : x))} data-testid={`shift-start-${i}`} />
                  <span className="text-slate-400">to</span>
                  <Input type="time" value={r.end} onChange={(e) => setRows(rs => rs.map((x, j) => j === i ? { ...x, end: e.target.value } : x))} data-testid={`shift-end-${i}`} />
                  {rows.length > 1 && <Button size="icon" variant="ghost" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setRows(rs => [...rs, { start: "", end: "" }])} data-testid="add-another-shift"><Plus className="h-4 w-4 mr-1" /> Add Another Shift</Button>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={createShifts} disabled={!tid} className="bg-blue-900 hover:bg-blue-950" data-testid="create-shifts">Create shifts</Button>
                <WeeklyDialog tid={tid} onDone={loadShifts} />
                <Button variant="outline" onClick={() => markDay("leave")} disabled={!tid} data-testid="mark-leave">Mark Leave</Button>
                <Button variant="outline" onClick={() => markDay("holiday")} disabled={!tid} data-testid="mark-holiday">Mark Holiday</Button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Scheduled shifts</p>
              {shifts.length === 0 ? <p className="text-sm text-slate-500">No shifts for this teacher yet.</p> : (
                <div className="space-y-2 max-h-[28rem] overflow-y-auto" data-testid="admin-shifts-list">
                  {shifts.map(s => (
                    <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5 text-sm" data-testid={`admin-shift-${s.id}`}>
                      <span>{formatDate(s.date)} · {s.type === "shift" ? `${s.start}–${s.end}` : <Badge variant="outline">{s.type}</Badge>} {s.status === "cancelled" && <Badge variant="outline" className="text-red-600 border-red-300 ml-1">cancelled</Badge>}</span>
                      {s.status === "active" && s.type === "shift" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => cancelShift(s.id)} data-testid={`cancel-shift-${s.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* REQUESTS */}
        <TabsContent value="requests">
          {requests.length === 0 ? <p className="text-sm text-slate-500 py-6">No shift-change requests.</p> : (
            <div className="space-y-3" data-testid="admin-change-requests">
              {requests.map(r => (
                <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`change-request-${r.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{r.teacher_name} · {formatDate(r.date)}</p>
                      <p className="text-sm text-slate-600">{r.original_start}–{r.original_end} → <b>{r.requested_start}–{r.requested_end}</b></p>
                      <p className="text-sm text-slate-500">Reason: {r.reason}</p>
                    </div>
                    <Badge className={r.status === "approved" ? "bg-green-600" : r.status === "rejected" ? "bg-red-600" : "bg-amber-500"}>{r.status}</Badge>
                  </div>
                  {r.status === "pending" && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => decide(r.id, "approve")} data-testid={`approve-change-${r.id}`}><Check className="h-4 w-4 mr-1" /> Approve</Button>
                      <EditApproveDialog req={r} onDone={() => { loadBase(); loadShifts(); }} />
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => decide(r.id, "reject")} data-testid={`reject-change-${r.id}`}><X className="h-4 w-4 mr-1" /> Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ALL TEACHERS — DAILY */}
        <TabsContent value="daily">
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="daily-panel">
            <div className="flex flex-wrap items-end gap-3 mb-3">
              <div><Label className="mb-1 block text-sm font-semibold">Date (IST)</Label><Input type="date" value={dailyDate} onChange={(e) => setDailyDate(e.target.value)} className="w-44" data-testid="daily-date" /></div>
              <Button onClick={runDaily} disabled={dailyBusy} className="bg-blue-900 hover:bg-blue-950" data-testid="run-daily">{dailyBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Users className="h-4 w-4 mr-1" /> Show all teachers</>}</Button>
              <Button variant="outline" onClick={downloadDailyCsv} disabled={!daily} data-testid="daily-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
              <Button variant="outline" onClick={printDaily} disabled={!daily} data-testid="daily-print"><Printer className="h-4 w-4 mr-1" /> Print</Button>
            </div>
            <p className="text-xs text-slate-500 mb-4">All teachers for the selected PTG day (Asia/Kolkata). A <b>missing clock-in</b> is shown for review and is <b>not</b> an automatic absence. Times/worked hours are never reduced by location flags. <span className="text-red-600 font-semibold">Red</span> = recorded outside the permitted area ({daily?.radius_m ?? settings?.radius_m ?? 150} m).</p>

            {!daily ? <p className="text-sm text-slate-500">Pick a date and tap “Show all teachers”.</p> : daily.teachers.length === 0 ? <p className="text-sm text-slate-500">No teachers found.</p> : (
              <div className="space-y-3" data-testid="daily-list">
                {daily.teachers.map((t) => (
                  <div key={t.teacher_id} className="rounded-xl border border-slate-200 p-3" data-testid={`daily-teacher-${t.teacher_id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <p className="font-semibold text-slate-900 break-words">{t.teacher_name}</p>
                      <Badge className="bg-blue-900 shrink-0" data-testid={`daily-total-${t.teacher_id}`}>Daily total: {t.daily_total_label}</Badge>
                    </div>
                    <div className="space-y-2">
                      {t.shifts.map((s, i) => (
                        <div key={i} className="rounded-lg bg-slate-50 border border-slate-200 p-2.5" data-testid={`daily-shift-${t.teacher_id}-${i}`}>
                          {s.status === "no_schedule" ? (
                            <p className="text-sm text-slate-500">No scheduled shift</p>
                          ) : s.shift_type === "leave" || s.shift_type === "holiday" ? (
                            <Badge variant="outline" className="text-slate-600">{s.status === "leave" ? "On leave" : "Holiday"}</Badge>
                          ) : (
                            <>
                              <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                                <span className="text-sm font-semibold text-slate-800">Scheduled {s.start}–{s.end}</span>
                                <Badge className={`shrink-0 ${s.status === "completed" ? "bg-green-600" : s.status === "missing_clock_in" ? "bg-amber-500" : s.status === "incomplete" ? "bg-orange-500" : "bg-slate-400"}`}>
                                  {s.status === "completed" ? "Completed" : s.status === "missing_clock_in" ? "Missing clock-in" : s.status === "incomplete" ? "No clock-out" : s.status}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-sm">
                                <div><span className="text-xs text-slate-400 block">Clock-in</span><DailyTime time={s.clock_in} loc={s.clock_in_loc} /></div>
                                <div><span className="text-xs text-slate-400 block">Clock-out</span><DailyTime time={s.clock_out} loc={s.clock_out_loc} /></div>
                                <div><span className="text-xs text-slate-400 block">Worked</span><span className="text-slate-700">{s.worked_label || "—"}</span></div>
                                <div><span className="text-xs text-slate-400 block">Late / Early</span>
                                  <span className="text-slate-700">
                                    {s.late_min ? <span className="text-amber-700 font-semibold">Late {s.late_min}m</span> : null}
                                    {s.late_min && s.early_min ? " · " : null}
                                    {s.early_min ? <span className="text-amber-700 font-semibold">Left {s.early_min}m early</span> : null}
                                    {!s.late_min && !s.early_min ? "—" : null}
                                  </span>
                                </div>
                              </div>
                              {(s.clock_in_evidence || s.clock_out_evidence) && (
                                <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid={`evidence-wrap-${t.teacher_id}-${i}`}>
                                  <AttendanceEvidence label="Clock-In" ev={s.clock_in_evidence} />
                                  <AttendanceEvidence label="Clock-Out" ev={s.clock_out_evidence} />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* REPORTS */}
        <TabsContent value="reports">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div><Label className="mb-1 block text-sm font-semibold">Teacher</Label>
                <Select value={tid} onValueChange={setTid}><SelectTrigger className="w-48" data-testid="report-teacher"><SelectValue /></SelectTrigger>
                  <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.email}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="mb-1 block text-sm font-semibold">From</Label><Input type="date" value={rFrom} onChange={(e) => setRFrom(e.target.value)} data-testid="report-from" /></div>
              <div><Label className="mb-1 block text-sm font-semibold">To</Label><Input type="date" value={rTo} onChange={(e) => setRTo(e.target.value)} data-testid="report-to" /></div>
              <Button onClick={runReport} className="bg-blue-900 hover:bg-blue-950" data-testid="run-report">Run report</Button>
              <Button variant="outline" onClick={downloadCsv} data-testid="download-csv"><Download className="h-4 w-4 mr-1" /> CSV</Button>
            </div>
            {report && (
              <>
                {report.totals.length > 0 && <p className="text-sm mb-2 text-slate-700" data-testid="report-totals">Total hours: {report.totals.map(t => `${t.teacher_name}: ${t.total_hours}h`).join(" · ")}</p>}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="report-table">
                    <thead><tr className="text-left text-slate-500 border-b"><th className="py-2 pr-3">Teacher</th><th className="pr-3">Date</th><th className="pr-3">Shift</th><th className="pr-3">In</th><th className="pr-3">Out</th><th className="pr-3">Status</th><th>Hours</th></tr></thead>
                    <tbody>
                      {report.rows.map((r, i) => (
                        <tr key={i} className="border-b last:border-0"><td className="py-2 pr-3">{r.teacher_name}</td><td className="pr-3">{formatDate(r.date)}</td><td className="pr-3">{r.start}–{r.end}</td><td className="pr-3">{r.clock_in || "—"}</td><td className="pr-3">{r.clock_out || "—"}{r.clock_out_flagged ? " ⚠" : ""}</td><td className="pr-3">{r.status}</td><td>{r.hours ?? "—"}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ManualCorrectDialog teachers={teachers} shifts={shifts} onDone={runReport} />
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function WeeklyDialog({ tid, onDone }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState([]);
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [s1, setS1] = useState("08:00"); const [e1, setE1] = useState("10:00");
  const names = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const submit = async () => {
    try { const { data } = await api.post("/admin/shifts/weekly", { teacher_id: tid, weekdays: days, start_date: start, end_date: end, shifts: [{ start: s1, end: e1 }] }); toast.success(`${data.created} shifts created`); setOpen(false); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" disabled={!tid} data-testid="weekly-btn">Weekly repeat</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Repeating weekly schedule</DialogTitle><DialogDescription>Creates the same shift on chosen weekdays across a date range.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1">{names.map((n, i) => <button key={i} onClick={() => setDays(d => d.includes(i) ? d.filter(x => x !== i) : [...d, i])} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${days.includes(i) ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`weekday-${i}`}>{n}</button>)}</div>
          <div className="grid grid-cols-2 gap-3"><div><Label className="mb-1 block text-sm">From</Label><Input type="date" value={start} onChange={e => setStart(e.target.value)} /></div><div><Label className="mb-1 block text-sm">To</Label><Input type="date" value={end} onChange={e => setEnd(e.target.value)} /></div></div>
          <div className="grid grid-cols-2 gap-3"><div><Label className="mb-1 block text-sm">Start</Label><Input type="time" value={s1} onChange={e => setS1(e.target.value)} /></div><div><Label className="mb-1 block text-sm">End</Label><Input type="time" value={e1} onChange={e => setE1(e.target.value)} /></div></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={days.length === 0} className="bg-blue-900 hover:bg-blue-950" data-testid="weekly-submit">Create schedule</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditApproveDialog({ req, onDone }) {
  const [open, setOpen] = useState(false);
  const [fs, setFs] = useState(req.requested_start);
  const [fe, setFe] = useState(req.requested_end);
  const submit = async () => {
    try { await api.post(`/admin/shift-changes/${req.id}/decision`, { decision: "edit_approve", final_start: fs, final_end: fe }); toast.success("Approved with edited timing"); setOpen(false); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`edit-approve-${req.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit & Approve</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Edit & Approve</DialogTitle><DialogDescription>Set the final approved timing for this shift.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3"><div><Label className="mb-1 block text-sm">Start</Label><Input type="time" value={fs} onChange={e => setFs(e.target.value)} data-testid="ea-start" /></div><div><Label className="mb-1 block text-sm">End</Label><Input type="time" value={fe} onChange={e => setFe(e.target.value)} data-testid="ea-end" /></div></div>
        <DialogFooter><Button onClick={submit} className="bg-green-600 hover:bg-green-700" data-testid="ea-submit">Approve</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualCorrectDialog({ teachers, shifts, onDone }) {
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState("");
  const [action, setAction] = useState("clock_in");
  const [dt, setDt] = useState("");
  const [reason, setReason] = useState("");
  const submit = async () => {
    const s = shifts.find(x => x.id === sid);
    if (!s) { toast.error("Pick a shift"); return; }
    try { const { data } = await api.post("/admin/attendance/correct", { teacher_id: s.teacher_id, shift_id: sid, action, ist_datetime: dt, reason }); toast.success(data.message); setOpen(false); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="mt-4" data-testid="manual-correct-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Manual correction</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Manual attendance correction</DialogTitle><DialogDescription>Original records are always retained in the audit history. A reason is required.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mb-1 block text-sm font-semibold">Shift</Label>
            <Select value={sid} onValueChange={setSid}><SelectTrigger data-testid="mc-shift"><SelectValue placeholder="Select shift" /></SelectTrigger>
              <SelectContent>{shifts.filter(s => s.type === "shift").map(s => <SelectItem key={s.id} value={s.id}>{formatDate(s.date)} {s.start}–{s.end}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="mb-1 block text-sm font-semibold">Action</Label>
            <Select value={action} onValueChange={setAction}><SelectTrigger data-testid="mc-action"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="clock_in">Clock In</SelectItem><SelectItem value="clock_out">Clock Out</SelectItem></SelectContent></Select></div>
          <div><Label className="mb-1 block text-sm font-semibold">Date & time (IST)</Label><Input type="datetime-local" value={dt} onChange={e => setDt(e.target.value)} data-testid="mc-datetime" /></div>
          <div><Label className="mb-1 block text-sm font-semibold">Reason</Label><Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} data-testid="mc-reason" /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="bg-blue-900 hover:bg-blue-950" data-testid="mc-submit">Save correction</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
